/**
 * Marketing dashboard — the ACQUISITION story (2026-06-12 IA: customer value
 * and retention live on their own pages; Exec is the only duplicator). Real cards are clickable (→ drill-down with filters); cards needing an
 * input (marketing spend / CM / channel) show a notice + a link to provide it.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GeoSection } from "../components/GeoSection.js";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { useIsViewer } from "../lib/use-role.js";
import { useConnectedEsp } from "../lib/integration-catalog.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { MetricCard } from "../components/MetricCard.js";
import { RankedBars } from "../components/RankedBars.js";
import { Segmented } from "../components/Segmented.js";
import { useDrill } from "../lib/drill.js";
import { usePeriod } from "../lib/period.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import type { DrillMetric, Dimension, DrillWindow } from "../components/DrillExplorer.js";
import { Card, CardHead, Explain, moneyFmt } from "../components/cards.js";
import { TrendChart } from "../components/TrendChart.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { DataTrustCardNotice } from "../lib/data-trust-banner.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { buildSkuDisplayByCode, useProductSkusQuery } from "../components/ListControls.js";
import { pageLoadState } from "../lib/page-load-state.js";

export interface Overview { metrics: { current: { revenueBase: number; revenueGrossBase: number; cmBase: number; cmPercent: number | null; orderCount: number; customerCount: number; newCustomerCount: number }; changes: { revenuePct: number | null; cmPct: number | null; orderCountPct: number | null } }; series?: Array<{ date: string; revenue: number; cm: number | null; orders: number }> }
interface LtvCac { ratio: number | null; ltv: number | null; cac: number | null; markerDays: number | null; cohortStart: string | null; cohortEnd: string | null; customers: number }
export interface ChannelCac { channel: string; spendBase: number; newCustomers: number; cac: number | null; newCustomerRevenueBase: number; newCustomerFirstOrderCmBase: number }
interface CacMetrics { affiliateCac: number | null; nonAffiliateCac: number | null; blendedCac: number | null; totalNewCustomers: number; totalAcquisitionCostBase: number; totalMarketingSpendBase: number; manualAdSpendBase: number; affiliateCommissionsBase: number; firstOrderAffiliateCommissionsBase: number; unallocatedAffiliateCommissionsBase: number; affiliateCommissionCoverage: { source: "contractual_ledger" | "matched_only" | "unavailable"; provisional: boolean; pendingBase: number; unallocatedRows: number; pendingRows: number; duplicateRowsSuppressed: number }; byChannel: ChannelCac[]; newCustomerFirstOrderCmBase: number; newCustomerFirstOrderRevenueBase: number; windowDays: number; trailingIncomplete: boolean }
export interface CacResponse {
  /** Raw picker window. Kept for financial reconciliation outside this section. */
  cac: CacMetrics;
  /** Acquisition cards use the spend-covered slice. null when these dates predate tracking. */
  acquisitionCac: CacMetrics | null;
  acquisitionOverview: Overview["metrics"] | null;
  coverage: { start: string | null; clamped: boolean; covered: boolean; effectiveStartsAt: string; effectiveEndsAt: string; note: string | null };
  ltvCacRatio: number | null;
  grossLtvCac: LtvCac;
  cmLtvCac: LtvCac;
  ltvCohortNote?: string;
}

export function acquisitionCoveragePresentation(
  coverage: CacResponse["coverage"] | undefined,
  fallbackWindowLabel: string,
): { windowLabel: string; basisSuffix: string; note: string | null } {
  if (!coverage?.clamped) {
    return { windowLabel: fallbackWindowLabel, basisSuffix: "", note: coverage?.note ?? null };
  }

  // `coverage.start` is the account's earliest tracked month, not proof that
  // this request was clamped at the start. An end-only clamp must not read
  // "since Sep 2020" when the effective request still starts in 2025.
  const trackingStart = coverage.start ? Date.parse(coverage.start) : Number.NaN;
  const effectiveStart = Date.parse(coverage.effectiveStartsAt);
  const startsAtTrackingBoundary = Number.isFinite(trackingStart)
    && trackingStart === effectiveStart;
  const coverageStartLabel = startsAtTrackingBoundary && coverage.start
    ? new Date(coverage.start).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : null;

  return {
    windowLabel: coverageStartLabel
      ? `since ${coverageStartLabel}, when cost tracking starts`
      : fallbackWindowLabel,
    basisSuffix: coverageStartLabel
      ? ` · since ${coverageStartLabel}`
      : " · dates shown above",
    // The server formats the exact effective range in the account's timezone.
    // Keep it authoritative instead of rebuilding dates in the browser.
    note: coverage.note,
  };
}

export function averageOrderValues(
  current: Overview["metrics"]["current"] | undefined,
): { aov: number | undefined; cmAov: number | undefined } {
  if (!current) return { aov: undefined, cmAov: undefined };
  if (current.orderCount === 0) return { aov: 0, cmAov: 0 };
  return {
    aov: current.revenueBase / current.orderCount,
    cmAov: current.cmBase / current.orderCount,
  };
}

export function BuyGoodsMarketingUnavailable(): React.ReactElement {
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title="Marketing results are unavailable" />
      <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13, lineHeight: 1.55 }}>
        This BuyGoods history does not prove all source fees, affiliate commissions, or refund reversals. CAC, MER, aMER, marketing spend, channel results, and LTV:CAC stay unavailable. Sales, orders, customers, average order value, and product revenue above remain provisional.
      </div>
    </Card>
  );
}

export function BuyGoodsQuantityUnavailable({ periodLabel, detail }: { periodLabel: string; detail: string }): React.ReactElement {
  return (
    <Card style={{ marginBottom: 0, gridColumn: "span 2" }}>
      <CardHead title={`Units sold · ${periodLabel}`} />
      <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13, lineHeight: 1.5 }}>
        This number is unavailable. {detail}
      </div>
    </Card>
  );
}
interface Variance {
  monthly: Array<{ month: string; claimed: number; collected: number; conversions: number; matched: number; variancePct: number | null }>;
  byCampaign: Array<{ campaignId: string; claimed: number; collected: number; conversions: number; matched: number; variancePct: number | null }>;
  totals: { claimed: number; collected: number; conversions: number; matched: number; matchRatePct: number | null; variancePct: number | null };
}

export interface Extras { rfm: Array<{ segment: string; customers: number; revenue: number; avgOrders: number; avgRecencyDays: number }>; atRisk: { customers: number; revenueAtRisk: number } }


export default function MarketingPage({
  initialProductGrouping = "product",
  initialUnitGrouping = "product",
}: {
  initialProductGrouping?: "sku" | "product";
  initialUnitGrouping?: "sku" | "product";
} = {}): React.ReactElement {
  const api = useApiClient();
  const productSkusQ = useProductSkusQuery();
  const skuDisplayByCode = useMemo(
    () => buildSkuDisplayByCode(productSkusQ.data),
    [productSkusQ.data],
  );
  const gate = useSetupGate();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const drill = useDrill();
  const period = usePeriod();
  const demo = useDemoMode();
  const [demandMode, setDemandMode] = useStickyRevenueCmMode("dd-mode-marketing-demand");
  const [productMoneyMode, setProductMoneyMode] = useStickyRevenueCmMode("dd-mode-marketing-products");
  const [channelMoneyMode, setChannelMoneyMode] = useStickyRevenueCmMode("dd-mode-marketing-channels");

  const overview = useQuery({ queryKey: ["mk-overview", period.cacheKey], enabled: ready, queryFn: () => api.get<Overview>("/dashboard/overview", { ...period.query }) });
  // Company-wide overview for the BLENDED ratios (MER / CM-MER / breakeven): their
  // denominator is account-level marketing spend, which isn't tracked per product,
  // so the numerator must stay whole-company too — else a product filter would
  // divide filtered revenue by total spend = a mismatched ratio (founder's rule).
  // Overriding every rail filter to undefined opts this request out of them for THIS
  // request only (product AND the first-touch source/affiliate/discount filters).
  const capsQ = useQuery({ queryKey: ["capabilities"], enabled: ready, queryFn: () => api.get<Capabilities>("/dashboard/capabilities"), staleTime: 0, refetchOnWindowFocus: true });
  const detailedSourceGap = capsQ.data?.buyGoodsDetailedHistoryGap === true;
  const expenseReady = demo || capsQ.data?.financialBasis === "profit";
  const derivedMarketingReady = expenseReady;
  const grossCustomerReady = capsQ.data?.grossCustomerValue === true;
  const quantityReady = capsQ.data?.physicalQuantity === true;
  const overviewCompany = useQuery({ queryKey: ["mk-overview-co", period.cacheKey], enabled: ready && derivedMarketingReady, queryFn: () => api.get<Overview>("/dashboard/overview", { ...period.query, product: undefined, acqSource: undefined, acqAffiliate: undefined, acqDiscount: undefined }) });
  const cacQ = useQuery({ queryKey: ["mk-cac", period.cacheKey], enabled: ready && derivedMarketingReady, queryFn: () => api.get<CacResponse>("/dashboard/cac", { ...period.query }) });
  const extras = useQuery({ queryKey: ["mk-extras"], enabled: ready && grossCustomerReady, queryFn: () => api.get<Extras>("/dashboard/marketing-extras") });
  const cmQueriesReady = Boolean(capsQ.data?.contributionMargin && !capsQ.data.cmGap);
  // M1: both LTV:CAC ratios use one fixed mature customer group. Revenue and CM
  // swap only customer value; CAC is recalculated for those exact people.
  // Mini previews for the Demand rank cards — the audit found tiles holding
  // placeholder TEXT ("Top sellers") instead of data.
  // ALL period-scoped (founder bug 2026-06-12: charts ignored the 30d/90d/1Y
  // picker while tiles obeyed it — "same stats everywhere").
  // Top products/units key by family (default) or SKU — a shared toggle on both cards.
  // Each card owns its OWN SKUs/Products toggle (founder 2026-06-30: toggling one
  // must not flip the other — the shared toggle also reflowed the page on click).
  const [prodGroup, setProdGroup] = useState<"sku" | "product">(initialProductGrouping);
  const [unitGroup, setUnitGroup] = useState<"sku" | "product">(initialUnitGrouping);
  const topProductsMetric = productMoneyMode === "profit" && cmQueriesReady ? "cm" : "revenue";
  const topProducts = useQuery({ queryKey: ["mk-top-products", period.cacheKey, prodGroup, topProductsMetric], enabled: ready, queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric: topProductsMetric, dimension: "product", ...period.query, grouping: prodGroup }) });
  // "Units sold" = TRUE individual units (a 6-pack = 6), the `boxes` metric — not
  // line quantity (`units`, where a pack counts as 1). Fixes the old mislabel so it
  // matches the Products page and its own name (founder 2026-07-14, #15).
  const topUnits = useQuery({ queryKey: ["mk-top-units-boxes", period.cacheKey, unitGroup], enabled: ready && quantityReady, queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric: "boxes", dimension: "product", ...period.query, grouping: unitGroup }) });
  const groupToggle = (value: "sku" | "product", onChange: (v: "sku" | "product") => void): React.ReactElement => (
    <span onClick={(e) => e.stopPropagation()}><Segmented ariaLabel="Group by" size="sm" value={value} onChange={onChange} options={[{ value: "sku", label: "SKUs" }, { value: "product", label: "Products" }]} /></span>
  );
  const prodToggle = groupToggle(prodGroup, setProdGroup);
  const unitToggle = groupToggle(unitGroup, setUnitGroup);
  const readableSku = (skuCode: string): string => {
    const display = skuDisplayByCode[skuCode];
    return display
      ? [display.description, display.details, display.code].filter(Boolean).join(" · ")
      : `SKU ${skuCode}`;
  };
  // Revenue split by channel (attribution.source_channel; 'Unknown' = unattributed)
  // — real now, even with one channel (founder 2026-06-16: "should show it").
  const channelMetric = channelMoneyMode === "profit" && cmQueriesReady ? "cm" : "revenue";
  const revByChannel = useQuery({ queryKey: ["mk-rev-channel", period.cacheKey, channelMetric], enabled: ready && derivedMarketingReady, queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric: channelMetric, dimension: "channel", ...period.query }) });
  const varianceQ = useQuery({
    queryKey: ["attribution-variance", period.cacheKey],
    queryFn: () => api.get<Variance>("/dashboard/attribution-variance", { ...period.query }),
    enabled: ready && !demo && derivedMarketingReady && capsQ.data?.channel === true,
  });
  const gateLoad = pageLoadState(gate);
  const requiredLoad = pageLoadState(
    capsQ,
    overview,
    overviewCompany,
    extras,
    topProducts,
    topUnits,
    revByChannel,
    varianceQ,
  );
  const cacLoad = pageLoadState(cacQ);
  const productSkusLoad = pageLoadState(productSkusQ);
  const skuLabelsRequired = prodGroup === "sku" || (quantityReady && unitGroup === "sku");

  if (gateLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your marketing numbers"
        retrying={gateLoad.retrying}
        onRetry={gateLoad.retry}
      />
    );
  }
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your marketing numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  if (skuLabelsRequired && productSkusLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your product list"
        retrying={productSkusLoad.retrying}
        onRetry={productSkusLoad.retry}
      />
    );
  }
  if (skuLabelsRequired && productSkusQ.data === undefined) {
    return <p role="status" style={{ color: "var(--body)" }}>Loading product names…</p>;
  }

  const caps = capsQ.data;
  const hasCm = Boolean(caps?.financialBasis === "profit" && caps.contributionMargin && !caps.cmGap);
  const demandCm = demandMode === "profit" && hasCm;
  const productCm = topProductsMetric === "cm";
  const channelCm = channelMetric === "cm";
  const o = overview.data?.metrics;
  const { aov, cmAov } = averageOrderValues(o?.current);
  // Daily series from the SAME overview response — sparklines on the live KPI
  // row (founder 2026-06-12: "anywhere we can use a trendline"). Real data
  // only; the series carries no customer counts, so New customers gets none.
  const series = overview.data?.series;
  // Revenue-over-time chart data, aggregated from the daily series to months so
  // the CM line (founder 2026-07-14) ties to the headline cmBase. Revenue stays
  // net, exactly as before; CM is null for months with no costed orders.
  const revCmMonths = (() => {
    const m = new Map<string, { revenue: number; cm: number; hasCm: boolean }>();
    for (const pt of series ?? []) {
      const key = pt.date.slice(0, 7);
      const cur = m.get(key) ?? { revenue: 0, cm: 0, hasCm: false };
      cur.revenue += pt.revenue;
      if (pt.cm !== null) { cur.cm += pt.cm; cur.hasCm = true; }
      m.set(key, cur);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, v]) => {
      const [y, mo] = key.split("-");
      const label = `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(mo) - 1]} ${String(y).slice(2)}`;
      return { label, a: v.revenue, b: v.hasCm ? v.cm : undefined };
    });
  })();
  const ordersSpark = series?.map((d) => d.orders);
  // Demand cards follow the GLOBAL period picker; the drill inherits the same
  // window so totals reconcile (M5: the product/units rank cards are period-
  // scoped, so their drills pass pageWindow too — they used to open all-time).
  const pageWindow: DrillWindow = period.drillWindow;
  const winLabel = period.label.toLowerCase();

  // Acquisition-efficiency math (all client-side divisions over served numbers).
  // These ratios are BLENDED (÷ company-wide marketing spend), so every numerator
  // uses the company-wide overview (`oc`), NOT the product-filtered `o` — they stay
  // whole-company when the rail filters to a product (matched populations).
  const coverage = cacQ.data?.coverage;
  const cac = coverage?.covered === false ? undefined : cacQ.data?.acquisitionCac ?? undefined;
  // When the picker reaches before acquisition-cost tracking, every acquisition
  // numerator and denominator starts at the same covered date. Demand and channel
  // cards above still follow the literal picker window.
  const oc = coverage?.clamped ? cacQ.data?.acquisitionOverview ?? undefined : overviewCompany.data?.metrics;
  // Two spend bases (founder's ratio-population-parity rule, 2026-07-13):
  //  • WHOLE-BUSINESS = ALL affiliate commissions + ad spend → MER, CM-MER, the
  //    Marketing-spend tile ("everything you spent").
  //  • ACQUISITION = confirmed first-order commissions + unallocated contractual
  //    commissions + ad spend → aMER, CM-aMER, and blended CAC. Known repeat-order
  //    commissions are retention cost and stay only in the whole-business number.
  const marketingSpend = cac?.totalMarketingSpendBase ?? 0; // all commissions + ad spend
  const acquisitionSpend = cac?.totalAcquisitionCostBase ?? 0; // confirmed first-order + unallocated commissions + ad spend
  // MER numerator = gross-of-refunds, tax-out revenue (founder 2026-06-25), so MER
  // matches how it's benchmarked everywhere else. AOV + the revenue tile keep revenueBase.
  const revenueNow = oc?.current.revenueGrossBase ?? 0;
  const newRev = cac?.newCustomerFirstOrderRevenueBase ?? 0;
  const ratio = (a: number, b: number): string | undefined => (b > 0 ? `${(a / b).toFixed(2)}×` : undefined);
  const mer = marketingSpend > 0 ? ratio(revenueNow, marketingSpend) : undefined;
  const amer = acquisitionSpend > 0 && cac ? ratio(newRev, acquisitionSpend) : undefined;
  const cmMer = marketingSpend > 0 && oc && hasCm ? ratio(oc.current.cmBase, marketingSpend) : undefined;
  // CM-aMER = NEW-customer TRUE first-order contribution margin ÷ ACQUISITION spend
  // (flag 5, founder 2026-07-12): the actual margin summed off each new customer's
  // first order (cac.newCustomerFirstOrderCmBase), NOT new-customer revenue × a
  // blended company CM%. Only shown once CM is built (caps gate the card);
  // undefined otherwise so it never renders a blended fake.
  const cmAmer = acquisitionSpend > 0 && hasCm && cac ? ratio(cac.newCustomerFirstOrderCmBase, acquisitionSpend) : undefined;
  // CM-aMER itself breaks even at 1.0×. This is the equivalent REVENUE aMER
  // target at the current CM rate, so keep that basis explicit on screen.
  const revenueBreakeven = oc?.current.cmPercent && oc.current.cmPercent > 0 ? `${(1 / oc.current.cmPercent).toFixed(2)}×` : undefined;
  // Population-matched LTV:CAC (M1). The picker chooses the acquisition era;
  // LTV and CAC then use the exact same mature people inside it.
  const cmLc = cacQ.data?.cmLtvCac;
  const grossLc = cacQ.data?.grossLtvCac;
  const ltvCac = cmLc?.ratio !== null && cmLc?.ratio !== undefined ? `${cmLc.ratio.toFixed(1)} : 1` : undefined;
  const grossLtvCac = grossLc?.ratio !== null && grossLc?.ratio !== undefined ? `${grossLc.ratio.toFixed(1)} : 1` : undefined;
  const ltvMarker = grossLc?.markerDays ?? cmLc?.markerDays ?? null;
  const ltvHorizonLabel = ltvMarker ? (ltvMarker >= 365 ? "1-year" : `${ltvMarker}-day`) : "matched-horizon";
  // M2: blended CAC inflates on short, still-filling windows — caveat it.
  const cacUnsettled = !!cac?.trailingIncomplete && (cac?.windowDays ?? 99) <= 31;
  const cacCaveat = cacUnsettled ? " · recent days still settling" : "";
  const coveragePresentation = acquisitionCoveragePresentation(coverage, winLabel);
  const coverageSuffix = coveragePresentation.basisSuffix;
  const acquisitionWindowLabel = coveragePresentation.windowLabel;
  const affiliateCostNotes: string[] = [];
  if (cac?.affiliateCommissionCoverage.source === "matched_only" && cac.affiliateCommissionsBase !== 0) {
    affiliateCostNotes.push("matched affiliate sales only");
  }
  if ((cac?.unallocatedAffiliateCommissionsBase ?? 0) !== 0) {
    affiliateCostNotes.push(`${moneyFmt(cac?.unallocatedAffiliateCommissionsBase)} not fully matched to exact order costs yet`);
  }
  if ((cac?.affiliateCommissionCoverage.pendingBase ?? 0) !== 0) {
    affiliateCostNotes.push(`${moneyFmt(cac?.affiliateCommissionCoverage.pendingBase)} still pending`);
  }
  const affiliateCostSuffix = affiliateCostNotes.length > 0 ? ` · ${affiliateCostNotes.join(" · ")}` : "";
  const provisionalCacSuffix = (cac?.unallocatedAffiliateCommissionsBase ?? 0) !== 0
    ? ` · includes ${moneyFmt(cac?.unallocatedAffiliateCommissionsBase)} in affiliate costs not fully matched to exact orders yet`
    : "";
  const open =
    (title: string, metric: DrillMetric, dimension?: Dimension, period?: DrillWindow, filters?: Parameters<typeof drill.open>[0]["filters"]) =>
    () =>
      drill.open({ title, metric, dimension, period, filters });
  // Blended ratios drill into the ratio OVER TIME (RatioDrill), not a one-sided
  // proxy — clicking MER shows MER, not revenue (founder 2026-06-14).
  const openRatio = (title: string, r: "mer" | "amer" | "cac" | "spend" | "cm-mer" | "cm-amer") => () =>
    drill.open({ title, metric: "revenue", ratio: r, period: pageWindow });

  return (
    <div>
      <div style={kicker}>Marketing</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>{expenseReady ? "Are we acquiring customers profitably?" : "How are sales moving?"}</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5, marginTop: 0 }}>
        {expenseReady
          ? "Acquisition efficiency, where demand comes from, and your affiliate program. Click any card to drill in and filter."
          : "Revenue, orders, customers, and products from your real sales. Click any card to explore."}
      </p>

      <SortableArea pageKey="marketing">
      <Hideable id="mkt-demand" label="Demand" span={12}>
      <Section title="Demand" sub="Revenue and orders — click to break down by month, product, state, or customer type.">
        {<Card style={{ marginBottom: 0, gridColumn: "1 / -1" }} onClick={open(`${demandCm ? "Contribution margin" : "Revenue"} · ${winLabel}`, demandCm ? "cm" : "revenue", "month", pageWindow)} className="dd-clickable-card" title="Click to explore">
          <CardHead title={`${demandCm ? "Contribution margin" : "Revenue"} over time · ${period.label}`} explain={["<b>What it is:</b> Revenue or contribution margin (CM) over time, with the period total and the change from the period before.", "<b>How it's calculated:</b> Revenue adds the selected orders after refunds and tax. CM uses those exact orders and dates, then subtracts each product's variable costs. If your store gives us one refund total for the whole order, each view subtracts only the part that belongs to the sales dollars it counts.", "<b>Example (sample data · year-to-date):</b> Revenue is $1,117,474. CM is $611,997 after the costs tied to those sales.", "<b>Note:</b> Pick a product to narrow both views. CM turns on after every selling product has costs. The current month is still filling in, so it usually looks low."]}>
            <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span className="dd-num" style={{ fontSize: 22, fontWeight: 700, color: "var(--num)" }}>{o ? moneyFmt(demandCm ? o.current.cmBase : o.current.revenueBase) : "—"}</span>
              {(() => { const change = demandCm ? o?.changes.cmPct : o?.changes.revenuePct; return change !== null && change !== undefined ? (
                <span className="dd-num" style={{ fontSize: 12.5, fontWeight: 700, color: change >= 0 ? "var(--good)" : "var(--bad)" }}>{change >= 0 ? "▲" : "▼"} {Math.abs(change * 100).toFixed(1)}%</span>
              ) : null; })()}
              <RevenueCmToggle mode={demandCm ? "profit" : "revenue"} setMode={setDemandMode} cmDisabled={!hasCm} />
            </span>
          </CardHead>
          <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
            <TrendChart loading={overview.isLoading || overview.isPlaceholderData} kind="line" switchKinds={["line", "area", "step", "table", "number"]} chartKey={demandCm ? "cm-over-time" : "revenue-over-time"} points={revCmMonths.map((point) => ({ label: point.label, a: (demandCm ? point.b : point.a) ?? 0 }))} />
          </div>
        </Card>}
        {<MetricCard id="orders" loading={overview.isLoading || overview.isPlaceholderData} title={`Orders · ${period.label}`} value={o ? o.current.orderCount.toLocaleString() : "—"} delta={o?.changes.orderCountPct} sub="vs prior period" spark={ordersSpark} caps={caps} trendMetric="orders" onClick={open(`Orders · ${winLabel}`, "orders", "month", pageWindow)} explain={["<b>What it is:</b> The total number of orders placed in the window. The arrow compares it to the same-length period just before.", "<b>How it's calculated:</b> A simple count of every order in the period.", "<b>Example (sample data · year-to-date):</b> 15,018 orders, ▼ 1.7% vs the prior period.", "<b>Note:</b> A subscription rebill counts as an order. Product-scopable in the drill, where you can slice by month, product, state, or customer type."]} />}
        {<MetricCard id="new-customers" loading={overview.isLoading || overview.isPlaceholderData} title={`New customers · ${period.label}`} value={o ? o.current.newCustomerCount.toLocaleString() : "—"} sub={o ? `${o.current.customerCount.toLocaleString()} total` : undefined} caps={caps} trendMetric="new_customers" onClick={open(`New customers · ${winLabel}`, "new_customers", "month", pageWindow)} explain={["<b>What it is:</b> First-time customers acquired in the window, with your total active customer count underneath.", "<b>How it's calculated:</b> Distinct customers whose first-ever order falls inside the window; the sub-number is everyone who ordered during the window. With a product selected, the first-ever order must include that product.", "<b>Example (sample data · year-to-date):</b> 5,065 new customers, with 7,342 total.", "<b>Note:</b> With no product selected, this is the exact customer count used in Blended Customer Acquisition Cost (CAC). A product selection narrows this card, but not Blended CAC. Someone who bought the selected product later counts as returning for that product, not new."]} />}
        {<AovCard
          aov={aov}
          cmAov={cmAov}
          loading={overview.isLoading}
          winLabel={winLabel}
          caps={caps}
          onRevenueDrill={open(`Average order value · ${winLabel}`, "aov", "month", pageWindow)}
          onCmDrill={open(`Average contribution margin per order · ${winLabel}`, "cm_aov", "month", pageWindow)}
        />}
        {<Card style={{ marginBottom: 0, gridColumn: "span 2" }} onClick={open(`${productCm ? "CM" : "Revenue"} by product · ${winLabel}`, productCm ? "cm" : "revenue", "product", pageWindow)} className="dd-clickable-card" title="Click to explore">
          <CardHead title={`${productCm ? "CM" : "Revenue"} by ${prodGroup === "sku" ? "SKU" : "product"} · ${period.label}`} explain={["<b>What it is:</b> Which products drive your revenue or contribution margin (CM), shown as the top 3.", "<b>How it's calculated:</b> Revenue adds each paid item's sales. CM uses those same items and dates, then subtracts that SKU's variable costs. Both can be grouped by product or SKU.", "<b>Example (sample data · year-to-date, Revenue):</b> Daily Greens $420,163, Deep Sleep $244,802, Radiant Collagen $128,458.", "<b>Note:</b> Item-level revenue leaves out shipping, so it can differ from the headline Revenue card. CM turns on after every selling product has costs. The drill uses the same basis and dates as this card."]}>
            <RevenueCmToggle mode={productCm ? "profit" : "revenue"} setMode={setProductMoneyMode} cmDisabled={!hasCm} />
            {prodToggle}
          </CardHead>
          <div style={{ marginTop: 12 }} />
          <RankedBars loading={topProducts.isLoading || topProducts.isPlaceholderData} rows={(topProducts.data?.rows ?? []).slice(0, 3).map((r) => ({ key: prodGroup === "sku" ? readableSku(r.key) : r.key, value: r.value }))} fmt={moneyFmt} max={3} />
        </Card>}
        {quantityReady && <Card style={{ marginBottom: 0, gridColumn: "span 2" }} onClick={open(`Units sold · ${winLabel}`, "boxes", "product", pageWindow)} className="dd-clickable-card" title="Click to explore">
          <CardHead title={`Units sold · ${period.label}`} explain={["<b>What it is:</b> The true number of individual units that went out the door for each product or SKU. A 6-pack counts as 6. The card shows the top 3.", "<b>How it's calculated:</b> Multiply each real catalog line by its pack size. A separate sample discounted to $0 counts because it shipped. Literal $0 rows for bottles already inside a paid pack stay out, so nothing is counted twice.", "<b>Example (sample data · Products):</b> Daily Greens leads on units thanks to its multi-packs, sitting well above its line-item count.", "<b>Note:</b> This uses the same basis as the Products page's Units sold card. Product / SKU changes the grouping. The drill breaks it down by SKU and month."]} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, marginBottom: 12 }}>{unitToggle}</div>
          <RankedBars loading={topUnits.isLoading || topUnits.isPlaceholderData} rows={(topUnits.data?.rows ?? []).slice(0, 3).map((r) => ({ key: unitGroup === "sku" ? readableSku(r.key) : r.key, value: r.value }))} fmt={(v: number) => v.toLocaleString()} money={false} max={3} />
        </Card>}
      </Section>
      </Hideable>

      {expenseReady && (capsQ.data === undefined ? (
        <Hideable id="mkt-source-check" label="Checking marketing sources" span={12}>
          <Card style={{ marginBottom: 0 }}>
            <CardHead title="Checking marketing sources" />
            <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13, lineHeight: 1.5 }}>
              Marketing results stay hidden until the source check finishes.
            </div>
          </Card>
        </Hideable>
      ) : detailedSourceGap ? (
        <Hideable id="mkt-buygoods-unavailable" label="Marketing results unavailable" span={12}>
          <BuyGoodsMarketingUnavailable />
        </Hideable>
      ) : <>
      <Hideable id="mkt-acquisition" label="Acquisition efficiency" span={12}>
      <Section title="Acquisition efficiency" sub={`How hard your marketing dollars work, ${acquisitionWindowLabel}. Enter your variable costs to unlock the CM views.${coveragePresentation.note ? ` ${coveragePresentation.note}` : ""}`}>
        {cacLoad.failed && (
          <AcquisitionLoadError
            hasSavedNumbers={cacQ.data !== undefined}
            retrying={cacLoad.retrying}
            onRetry={cacLoad.retry}
          />
        )}
        {(!cacLoad.failed || cacQ.data !== undefined) && <>
        {<MerCard mer={mer} cmMer={cmMer} basisSuffix={coverageSuffix} caps={caps} openRatio={openRatio} />}
        {<AmerCard amer={amer} cmAmer={cmAmer} revenueBreakeven={revenueBreakeven} basisSuffix={coverageSuffix} caps={caps} openRatio={openRatio} />}
        {<MetricCard title="Marketing spend" trustWarning={<DataTrustCardNotice card="Marketing spend" />} requires={["marketingSpend"]} caps={caps} companyWide trendRatio="spend" chartKey="spend" onClick={openRatio("Marketing spend", "spend")} value={cac && marketingSpend > 0 ? moneyFmt(marketingSpend) : undefined} sub={cac ? `incl. ${moneyFmt(cac.affiliateCommissionsBase)} affiliate commissions${affiliateCostSuffix}${coverageSuffix}` : undefined} sample={{ value: "$12,940", sub: "incl. $9,120 affiliate commissions" }} explain={["<b>What it is:</b> Everything you spent on marketing in this window. It includes your monthly ad spend and affiliate commissions.", "<b>How it's calculated:</b> Monthly ad spend, scaled to the dates shown, plus every commission your affiliate platform says you owe. Rejected commissions stay out. The cost still counts when it is not fully matched to an exact order cost.", "<b>Example (sample data · year-to-date):</b> $265,404 ad spend + $12,976 affiliate commissions = $278,380.", "<b>Note:</b> Blended, so a product filter will not move it. Costs that are not fully matched stay in the company total but do not get assigned to a channel. If your dates reach back before cost tracking began, this card starts at the first covered month and says so."]} />}
        {<MetricCard title="Blended CAC" trustWarning={<DataTrustCardNotice card="CAC" />} requires={["marketingSpend"]} caps={caps} companyWide value={cac?.blendedCac !== null && cac?.blendedCac !== undefined ? moneyFmt(cac.blendedCac) : undefined} sub={cac ? `${cac.totalNewCustomers.toLocaleString()} new customers${provisionalCacSuffix}${cacCaveat}${coverageSuffix}` : undefined} sample={{ value: "$24.80", sub: "486 new customers" }} trendRatio="cac" chartKey="cac" onClick={openRatio("CAC", "cac")} explain={["<b>What it is:</b> The average cost to acquire one new customer across all your channels.", "<b>How it's calculated:</b> Ad spend plus confirmed first-order affiliate commissions, plus acquisition costs that are not fully matched to exact orders yet. Divide that by the new customers acquired during the same dates. Known repeat-order commissions stay out because they are a retention cost.", "<b>Example (sample data · year-to-date):</b> $273,304 acquisition spend ÷ 5,065 new customers = $53.96.", "<b>Note:</b> Unallocated costs mean Blended CAC is not final yet. They never get assigned to a channel until the match is exact. If your dates reach back before cost tracking began, both spend and customers start at the first covered month. Missing spend never counts as $0." + (cacUnsettled ? " This window ends mid-month, so CAC is still settling. Widen the dates for a steadier read." : "") + " Click to see CAC over time."]} />}
        {<LtvCacCard grossLtvCac={grossLtvCac} ltvCac={ltvCac} ltvHorizonLabel={ltvHorizonLabel} matchedCac={grossLc?.cac ?? cmLc?.cac ?? null} matchedCustomers={grossLc?.customers ?? cmLc?.customers ?? 0} cohortNote={cacQ.data?.ltvCohortNote} caps={caps} />}
        </>}
        
        {<Card style={{ marginBottom: 0, gridColumn: "span 2" }} onClick={open(`${channelCm ? "CM" : "Revenue"} by channel`, channelCm ? "cm" : "revenue", "channel", pageWindow)} className="dd-clickable-card" title="Click to explore">
          <CardHead title={`${channelCm ? "CM" : "Revenue"} by channel · ${period.label}`} explain={["<b>What it is:</b> Revenue or contribution margin (CM) split by the channel credited for each order.", "<b>How it's calculated:</b> Revenue groups each order's sales by channel. CM uses those same orders and channels, then subtracts the variable costs tied to each order.", "<b>Example (sample data · year-to-date, Revenue):</b> Email $380,546, organic $256,331, Meta ads $204,832, direct $96,230, Google ads $94,502, affiliate $85,033.", "<b>Note:</b> Unknown means we could not tie the order to a channel. Pick a product to narrow both views. CM turns on after every selling product has costs."]}>
            <RevenueCmToggle mode={channelCm ? "profit" : "revenue"} setMode={setChannelMoneyMode} cmDisabled={!hasCm} />
          </CardHead>
          <RankedBars loading={revByChannel.isLoading || revByChannel.isPlaceholderData} rows={(revByChannel.data?.rows ?? []).slice(0, 6).map((r) => ({ key: r.key, value: r.value }))} fmt={moneyFmt} />
        </Card>}
      </Section>
      </Hideable>

      <Hideable id="mkt-geo" label="Geography" span={12}>{<GeoSection caps={caps} />}</Hideable>

      {/* Locked-wall collapse (2026-06-12 audit): six identical padlock tiles
          become two compact connect cards — or, when connected, the affiliate
          numbers live in Attribution below. Hidden in the public demo. */}
      {(!caps?.channel || !caps?.email) && !demo ? (
        <Hideable id="mkt-connect" label="Connect prompts" span={12}><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginTop: 26 }}>
          {!caps?.channel && (
            <Link to="/connections" style={linkCard}>
              <div>
                <div style={linkTitle}>Connect your affiliate platform</div>
                <div style={linkSub}>Unlocks affiliate revenue, active affiliates, and affiliate CAC</div>
              </div>
              <span style={{ color: "var(--accent)", fontSize: 18 }}>→</span>
            </Link>
          )}
          {!caps?.email && (
            <Link to="/connections" style={linkCard}>
              <div>
                <div style={linkTitle}>Connect your email platform</div>
                <div style={linkSub}>Unlocks email revenue, list growth, and engagement</div>
              </div>
              <span style={{ color: "var(--accent)", fontSize: 18 }}>→</span>
            </Link>
          )}
        </div></Hideable>
      ) : null}

      {/* Attribution + Segments side by side at the bottom (founder 2026-06-16),
          with Overdue customers living inside Segments. */}
      <Hideable id="mkt-attribution-segments" label="Attribution & segments" span={12}>
      <section style={{ marginTop: 30, paddingTop: 22, borderTop: "1px solid var(--border)", display: "grid", gridTemplateColumns: demo ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: 24 }}>
        {!demo && (
        <div>
          <SectionHead title="Attribution" sub="What your platforms CLAIM they drove vs the dollars you actually collected." />
          <VarianceCard
            caps={caps}
            data={varianceQ.data}
            loading={varianceQ.isLoading || varianceQ.isPlaceholderData}
          />
        </div>
        )}
        <div>
          <SectionHead title="Segments" sub="RFM-style customer groups, plus who's slipping away — so you can act before they're gone." />
          <RfmCard rfm={extras.data?.rfm} atRisk={extras.data?.atRisk} />
        </div>
      </section>
      </Hideable>
      </>)}
      {!expenseReady && <>
        <Hideable id="mkt-geo" label="Geography" span={12}><GeoSection caps={caps} /></Hideable>
        {grossCustomerReady && (
          <Hideable id="mkt-segments" label="Customer segments" span={12}>
            <section style={{ marginTop: 30, paddingTop: 22, borderTop: "1px solid var(--border)" }}>
              <SectionHead title="Customer segments" sub="Who is buying, coming back, or starting to slip away." />
              <RfmCard rfm={extras.data?.rfm} atRisk={extras.data?.atRisk} />
            </section>
          </Hideable>
        )}
      </>}
      </SortableArea>

    </div>
  );
}

/** What each segment IS — the definition, not the play (founder 2026-06-16:
 *  "define what each of those are instead of saying what we should do"). */
const SEGMENT_DEFS: Record<string, string> = {
  Champions: "Bought recently, buy the most often, and spend the most — your best customers.",
  Loyal: "Repeat customers from the last year — your steady base (Overdue flags the ones slipping).",
  Promising: "Bought once in the last year — nurture them toward a second order.",
  Dormant: "Haven't bought in over a year — largely lapsed.",
};

/** What to DO with each segment — shown in the ⓘ next to its name (founder
 *  2026-06-16: definition on the row, the play in an info box beside it). */
const SEGMENT_PLAYS: Record<string, string> = {
  Champions: "Protect them — VIP perks, early access, never discount-spam.",
  Loyal: "Grow them — cross-sell, bundles, a subscription pitch.",
  Promising: "Get order #2 — nudge at their reorder window with a reason to return.",
  Dormant: "One last-chance offer, then stop paying to reach them.",
};


/** Praxis claimed-vs-collected report. Lives behind the channel capability. */
export function VarianceCard({
  caps,
  data,
  loading,
}: {
  caps?: Capabilities;
  data: Variance | undefined;
  loading: boolean;
}): React.ReactElement {
  if (!caps?.channel) {
    return (
      <MetricCard title="Platform over-reporting" requires={["channel"]} caps={caps}
        explain={["<b>What it is:</b> What each ad or affiliate platform claims it drove vs the dollars you actually collected — over-attribution made visible, by month and by campaign.", "<b>How it's calculated:</b> Each platform's claimed revenue compared to the revenue on the real orders it matched to, expressed as a percentage gap plus a match rate.", "<b>Example:</b> No live numbers in the demo — it stays locked until a channel is connected.", "<b>Note:</b> Blended — not product-scopable. Connect your affiliate platform (or an ad platform) to unlock it."]} />
    );
  }
  if (loading) {
    return (
      <Card style={{ marginBottom: 0, gridColumn: "span 2" }}>
        <CardHead title="Is your platform over-reporting?" />
        <div role="status" style={{ color: "var(--faint)", fontSize: 13, marginTop: 10 }}>Loading…</div>
      </Card>
    );
  }
  if (!data || data.totals.conversions === 0) {
    return (
      <Card style={{ marginBottom: 0, gridColumn: "span 2" }}>
        <CardHead title="Is your platform over-reporting?" explain={["<b>What it is:</b> What your platform claims it drove vs the dollars you actually collected.", "<b>How it's calculated:</b> Claimed revenue compared to the revenue on the real orders it matched to.", "<b>Example:</b> No numbers yet — conversions are still syncing from your first affiliate-platform pull.", "<b>Note:</b> Blended — not product-scopable. This fills in once the first sync completes."]} />
        <div style={{ color: "var(--faint)", fontSize: 13, marginTop: 10 }}>Conversions are syncing — this fills in shortly.</div>
      </Card>
    );
  }
  const t = data.totals;
  const v = t.variancePct;
  return (
    <Card style={{ marginBottom: 0, gridColumn: "span 2" }}>
      <CardHead title="Is your platform over-reporting?" explain={["<b>What it is:</b> Your affiliate platform's claimed revenue vs the dollars actually collected from matched orders over the same dates, with a match rate.", "<b>How it's calculated:</b> Claimed revenue minus collected revenue, divided by collected. Both sides are product dollars before shipping and tax. A matched order counts once, even if two conversions claim it.", "<b>Example:</b> Claimed $94,000 and collected $47,000 = +100% variance. If collected is $0, a percentage cannot be calculated; the card shows unmatched instead of a fake 0%.", "<b>Note:</b> Blended and not product-scopable. A low match rate signals a tracking gap. Months with no matched dollars break the variance line rather than being drawn as zero."]} />
      <div style={{ fontSize: 13, color: "var(--body)", margin: "8px 0 10px", lineHeight: 1.5 }}>
        Claimed <b className="dd-num" style={{ color: "var(--num)" }}>{moneyFmt(t.claimed)}</b> · collected{" "}
        <b className="dd-num" style={{ color: "var(--num)" }}>{moneyFmt(t.collected)}</b>
        {v !== null && (
          <> · <b className="dd-num" style={{ color: Math.abs(v) > 0.15 ? "var(--bad)" : "var(--good)" }}>{v >= 0 ? "+" : ""}{Math.round(v * 100)}%</b> variance</>
        )}
        {v === null && t.claimed > 0 && (
          <> · <b style={{ color: "var(--c2)" }}>variance not measurable until a claimed conversion matches collected dollars</b></>
        )}
        {t.matchRatePct !== null && <> · match rate <b className="dd-num">{Math.round(t.matchRatePct)}%</b></>}
      </div>
      <TrendChart kind="grouped-bars" seriesNames={["Claimed", "Collected"]}
        points={data.monthly.map((m) => ({ label: m.month, a: m.claimed, b: m.collected }))}
        lineOverlay={{ label: "Variance %", values: data.monthly.map((m) => m.variancePct === null ? null : m.variancePct * 100) }} />
      {data.byCampaign.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {data.byCampaign.slice(0, 5).map((cmp) => (
            <div key={cmp.campaignId} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--hair)", fontSize: 12.5 }}>
              <span style={{ color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Offer {cmp.campaignId}</span>
              <span className="dd-num" style={{ color: "var(--num)", whiteSpace: "nowrap" }}>
                {moneyFmt(cmp.claimed)} claimed · {moneyFmt(cmp.collected)} collected
                {cmp.variancePct !== null && <b style={{ color: Math.abs(cmp.variancePct) > 0.15 ? "var(--bad)" : "var(--good)", marginLeft: 6 }}>{cmp.variancePct >= 0 ? "+" : ""}{Math.round(cmp.variancePct * 100)}%</b>}
                {cmp.variancePct === null && cmp.claimed > 0 && <b style={{ color: "var(--c2)", marginLeft: 6 }}>unmatched</b>}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function RfmCard({ rfm, atRisk }: { rfm?: Extras["rfm"]; atRisk?: Extras["atRisk"] }): React.ReactElement {
  const isViewer = useIsViewer();
  const esp = useConnectedEsp();
  const [pendingPush, setPendingPush] = useState<string | null>(null);
  const max = Math.max(1, ...(rfm ?? []).map((s) => s.revenue));
  return (
    <Card style={{ marginBottom: 0, gridColumn: "span 2" }}>
      <CardHead title="Customer segments (RFM)" explain={["<b>What it is:</b> Your customers grouped by how recently and how often they buy and how much they spend — Champions, Loyal, Promising, and Dormant. Each group's definition sits under its name.", "<b>How it's calculated:</b> Per customer over all time — Champions bought in the last 90 days with 3+ orders; Loyal within a year with 2+; Promising within a year with 1; Dormant otherwise.", "<b>Example (sample data · all time):</b> Champions 1,889 ($826,872) · Loyal 3,737 ($1,253,824) · Promising 7,890 ($784,861) · Dormant 8,835 ($1,296,636).", "<b>Note:</b> All-time — it ignores the period picker, so these counts deliberately won't match the window's new/total customer cards. It DOES follow the sidebar product filter, showing the segments of customers acquired via the chosen product."]} />
      <div style={{ marginTop: 12 }}>
        {!rfm && <div style={{ color: "var(--faint)" }}>Loading…</div>}
        {rfm?.map((s) => (
          <div key={s.segment} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", borderBottom: "1px solid var(--hair)" }}>
            <div style={{ width: 230 }}>
              <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                {s.segment}
                <Explain title={s.segment} lines={[`<b>What to do:</b> ${SEGMENT_PLAYS[s.segment] ?? "—"}`]} />
                <span className="dd-num" style={{ color: "var(--num)", fontWeight: 500, fontSize: 12 }}>· {s.customers.toLocaleString()}</span>
              </div>
              <div style={{ color: "var(--faint)", fontSize: 11, marginTop: 2, lineHeight: 1.35 }}>{SEGMENT_DEFS[s.segment] ?? ""}</div>
            </div>
            <div style={{ flex: 1, height: 16, background: "var(--surface-2)", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${Math.max(1, (s.revenue / max) * 100)}%`, height: "100%", background: "var(--accent)", borderRadius: 5 }} />
            </div>
            <span className="dd-num" style={{ width: 86, textAlign: "right", color: "var(--num)", fontSize: 13, fontWeight: 600 }}>{moneyFmt(s.revenue)}</span>
            {!isViewer && esp && (
              <button onClick={() => setPendingPush(s.segment)} disabled={pendingPush !== null} title={`Push this segment to ${esp.label}`} style={pushBtn}>
                ↗ {esp.label}
              </button>
            )}
          </div>
        ))}
        {/* Overdue customers — same row shape as the RFM segments, with its own ⓘ.
            It's a habit-based cut (not RFM), which the ⓘ spells out. CSV export was
            removed 2026-07-07 (founder: "not doing emails — order-id CSVs are pointless"). */}
        {atRisk && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", borderBottom: "1px solid var(--hair)" }}>
            <div style={{ width: 230 }}>
              <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                Overdue customers
                <Explain title="Overdue customers" lines={["<b>What it is:</b> Repeat customers who are overdue against their own buying rhythm but not yet a full year gone — the win-back segment, timed to each customer's habits rather than a fixed window.", "<b>How it's calculated:</b> Customers with 2+ orders in the past year who have now gone past 1.5× their own average gap between orders.", "<b>Example (sample data · all time):</b> 3,033 overdue customers, with $1,241,348 of spend at risk.", "<b>Note:</b> All-time (ignores the period picker), but it follows the sidebar product filter — customers acquired via the chosen product. It's a habit-based cut, so it can overlap the Loyal and Champions segments (both are repeat buyers); it can never overlap Promising, which is a one-time buyer. Win them back now, before they lapse for good."]} />
                <span className="dd-num" style={{ color: "var(--num)", fontWeight: 500, fontSize: 12 }}>· {atRisk.customers.toLocaleString()}</span>
              </div>
              <div style={{ color: "var(--faint)", fontSize: 11, marginTop: 2, lineHeight: 1.35 }}>overdue vs their own cadence — spend at risk</div>
            </div>
            <div style={{ flex: 1, height: 16, background: "var(--surface-2)", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${Math.max(1, (atRisk.revenueAtRisk / max) * 100)}%`, height: "100%", background: "var(--accent)", borderRadius: 5 }} />
            </div>
            <span className="dd-num" style={{ width: 86, textAlign: "right", color: "var(--num)", fontSize: 13, fontWeight: 600 }}>{moneyFmt(atRisk.revenueAtRisk)}</span>
            {!isViewer && esp && (
              <button onClick={() => setPendingPush("Overdue")} disabled={pendingPush !== null} title={`Push overdue customers to ${esp.label}`} style={pushBtn}>
                ↗ {esp.label}
              </button>
            )}
          </div>
        )}
        {pendingPush && esp && (
          <PushModal
            segment={pendingPush}
            platform={esp.platform}
            espLabel={esp.label}
            onClose={() => setPendingPush(null)}
          />
        )}
      </div>
    </Card>
  );
}

const pushBtn: React.CSSProperties = {
  border: "1px solid color-mix(in srgb, var(--accent) 40%, var(--border))",
  background: "color-mix(in srgb, var(--accent) 12%, var(--surface-2))",
  color: "var(--accent)",
  borderRadius: 7,
  padding: "4px 9px",
  fontSize: 11.5,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

interface PushResult {
  audienceName: string;
  candidates: number;
  pushed: number;
  skipped: number;
  skippedByReason: { notInEsp: number; notOptedIn: number; unsubscribedOrBounced: number };
  espLabel: string;
}

const ONTRAPORT_APP_URL = "https://app.ontraport.com";

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modalBox: React.CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
  padding: 22, width: 460, maxWidth: "92vw",
};
const modalTitle: React.CSSProperties = { margin: "0 0 10px", fontSize: 16 };
const modalText: React.CSSProperties = { color: "var(--body)", fontSize: 13, lineHeight: 1.55, margin: "0 0 12px" };
const modalActions: React.CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 };
const modalCancelBtn: React.CSSProperties = {
  background: "transparent", color: "var(--body)", border: "1px solid var(--border)",
  padding: "8px 16px", borderRadius: 7, fontSize: 13, cursor: "pointer",
};
const modalConfirmBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--accent-contrast)", border: "none",
  padding: "8px 18px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer",
};

/**
 * Push-to-ESP modal: confirm (with the deliverability guidance) → pushing → a
 * PERSISTENT result (what landed + the skip breakdown + a link into the ESP), or
 * a clear error with Retry. (founder 2026-06-17: the old flash-and-vanish toast
 * hid the outcome — "it should confirm it was created and give you a link.")
 */
function PushModal({
  segment,
  platform,
  espLabel,
  onClose,
}: {
  segment: string;
  platform: string;
  espLabel: string;
  onClose: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const [phase, setPhase] = useState<"confirm" | "pushing" | "done">("confirm");
  const [result, setResult] = useState<PushResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The push keeps running even if they close the modal — a toast notifies on
  // completion either way (founder: "if they click out they should get a popup
  // saying it's ready"). The ref avoids setting state on an unmounted modal.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const run = async (): Promise<void> => {
    setPhase("pushing");
    setError(null);
    try {
      const r = await api.post<PushResult>(`/integrations/esp/${platform}/push`, { segment });
      toast.success(
        r.pushed > 0
          ? `Pushed ${r.pushed.toLocaleString()} contact${r.pushed === 1 ? "" : "s"} to ${espLabel} (“${r.audienceName}”).`
          : `Checked ${r.candidates.toLocaleString()} — nothing to tag in ${espLabel}. Open the result for the breakdown.`,
      );
      if (mounted.current) {
        setResult(r);
        setPhase("done");
      }
    } catch (e) {
      const msg = (e as { detail?: { error?: string } } | null)?.detail?.error ?? "Push failed — try again.";
      toast.error(`${espLabel} push failed: ${msg}`);
      if (mounted.current) {
        setError(msg);
        setPhase("done");
      }
    }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        {phase === "confirm" && (
          <>
            <h3 style={modalTitle}>Push “{segment}” to {espLabel}?</h3>
            <p style={modalText}>
              This tags everyone in this segment who's <b>already in {espLabel} and opted in</b>. We never
              add new contacts or change anyone's subscription, and unsubscribed or bounced contacts are
              skipped automatically.
            </p>
            <p style={modalText}>
              <b>For best deliverability:</b> this is <i>all</i> matching contacts, not just engaged ones —
              we don't have email open/click data (that lives in {espLabel}). Before you send, apply an{" "}
              <b>“active in the last 90 days”</b> engagement filter in {espLabel} so you only email people
              who still open your emails.
            </p>
            <div style={modalActions}>
              <button onClick={onClose} style={modalCancelBtn}>Cancel</button>
              <button onClick={() => void run()} style={modalConfirmBtn}>Push to {espLabel}</button>
            </div>
          </>
        )}

        {phase === "pushing" && (
          <>
            <h3 style={{ ...modalTitle, display: "flex", alignItems: "center" }}>
              <Spinner /> Pushing “{segment}” to {espLabel}…
            </h3>
            <p style={modalText}>
              Looking up each contact in {espLabel}, skipping anyone unsubscribed or not opted in, and
              tagging the rest. This can take a moment for a large segment.
            </p>
            <p style={{ ...modalText, fontSize: 12.5, color: "var(--faint)" }}>
              You can close this — we'll pop a notification when it's done.
            </p>
            <div style={modalActions}>
              <button onClick={onClose} style={modalCancelBtn}>Close</button>
            </div>
          </>
        )}

        {phase === "done" && result && (
          <>
            <h3 style={modalTitle}>
              {result.pushed > 0 ? `✓ Pushed to ${espLabel}` : `Nothing tagged in ${espLabel}`}
            </h3>
            <p style={modalText}>
              Checked <b>{result.candidates.toLocaleString()}</b> contact{result.candidates === 1 ? "" : "s"}{" "}
              from this segment against {espLabel}.
            </p>
            {result.candidates === 0 ? (
              <p style={modalText}>
                We couldn't read any email addresses for this segment from your store — so there was nothing
                to check. That's on our side, not your data; tell us and we'll dig in.
              </p>
            ) : result.pushed > 0 ? (
              <p style={modalText}>
                Tagged <b>{result.pushed.toLocaleString()}</b> contact{result.pushed === 1 ? "" : "s"} into
                the <b>“{result.audienceName}”</b> tag in {espLabel}. Filter your {espLabel} contacts by that
                tag to use the audience.
              </p>
            ) : (
              <p style={modalText}>
                None of them are opted-in {espLabel} contacts yet, so there was nothing to tag — see the
                breakdown below. (We never create new contacts or change consent.)
              </p>
            )}
            {result.skipped > 0 && (
              <p style={{ ...modalText, fontSize: 12.5, color: "var(--faint)" }}>
                Skipped {result.skipped.toLocaleString()} —{" "}
                {result.skippedByReason.notInEsp.toLocaleString()} not in {espLabel},{" "}
                {result.skippedByReason.notOptedIn.toLocaleString()} not opted in,{" "}
                {result.skippedByReason.unsubscribedOrBounced.toLocaleString()} unsubscribed/bounced.
              </p>
            )}
            <div style={modalActions}>
              {result.pushed > 0 && (
                <a href={ONTRAPORT_APP_URL} target="_blank" rel="noreferrer" style={{ ...modalConfirmBtn, textDecoration: "none" }}>
                  Open {espLabel} →
                </a>
              )}
              <button onClick={onClose} style={modalCancelBtn}>Done</button>
            </div>
          </>
        )}

        {phase === "done" && !result && (
          <>
            <h3 style={modalTitle}>Push failed</h3>
            <p style={{ ...modalText, color: "var(--bad)" }}>{error}</p>
            <p style={modalText}>Nothing was changed in {espLabel}. You can try again.</p>
            <div style={modalActions}>
              <button onClick={onClose} style={modalCancelBtn}>Close</button>
              <button onClick={() => void run()} style={modalConfirmBtn}>Retry</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Inline SVG spinner (SMIL-animated — no global CSS keyframes needed). */
function Spinner(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: 8, flexShrink: 0 }} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="var(--border)" strokeWidth="3" />
      <path d="M12 3 a9 9 0 0 1 9 9" fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }): React.ReactElement {
  return (
    <section style={{ marginTop: 30, paddingTop: 22, borderTop: "1px solid var(--border)" }}>
      <SectionHead title={title} sub={sub} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>{children}</div>
    </section>
  );
}

/** Shared section header — a small accent bar + title + sub. The accent bar +
 *  the section's top hairline divider are what visually separate the sections
 *  (founder 2026-06-16: "make each section more divided"). */
function SectionHead({ title, sub }: { title: string; sub?: string }): React.ReactElement {
  return (
    <div style={{ margin: "0 0 14px" }}>
      <h2 style={{ fontSize: 17, margin: 0, display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 4, height: 17, background: "var(--accent)", borderRadius: 2, display: "inline-block" }} />
        {title}
      </h2>
      {sub && <p style={{ color: "var(--body)", fontSize: 12.5, margin: "5px 0 0 13px" }}>{sub}</p>}
    </div>
  );
}

export function AcquisitionLoadError({
  hasSavedNumbers,
  retrying,
  onRetry,
}: {
  hasSavedNumbers: boolean;
  retrying: boolean;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div role="alert" style={{ gridColumn: "1 / -1", color: "var(--body)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 35%, var(--border))", borderRadius: "var(--r-md)", padding: "12px 15px", lineHeight: 1.55, fontSize: 13 }}>
      <div>
        <b>{hasSavedNumbers ? "We couldn’t refresh your acquisition numbers." : "We couldn’t load your acquisition numbers."}</b>{" "}
        {hasSavedNumbers
          ? "The numbers below are from the last successful load."
          : "Your sales data is still here. Try again to load marketing spend, CAC, MER, and aMER."}
      </div>
      <button type="button" disabled={retrying} onClick={onRetry} style={{ ...modalConfirmBtn, marginTop: 10, opacity: retrying ? 0.6 : 1 }}>
        {retrying ? "Trying again…" : "Try again"}
      </button>
    </div>
  );
}

const linkCard: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)",
  boxShadow: "var(--shadow-card)", padding: "16px 18px", textDecoration: "none",
};
const linkTitle: React.CSSProperties = { color: "var(--text)", fontSize: 14.5, fontWeight: 600 };
const linkSub: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, marginTop: 4 };

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };

/** LTV:CAC as ONE card with a Revenue/CM toggle (was two cards: revenue + CM).
 *  Revenue shows revenue-based LTV:CAC; CM shows contribution-margin LTV:CAC.
 *  Same cost-to-acquire in both; only the LTV (numerator) changes. */
function LtvCacCard({ grossLtvCac, ltvCac, ltvHorizonLabel, matchedCac, matchedCustomers, cohortNote, caps, footerLink }: {
  grossLtvCac?: string; ltvCac?: string; ltvHorizonLabel: string; matchedCac: number | null; matchedCustomers: number; cohortNote?: string; caps: React.ComponentProps<typeof MetricCard>["caps"];
  footerLink?: React.ComponentProps<typeof MetricCard>["footerLink"];
}): React.ReactElement {
  const hasCm = Boolean(caps?.contributionMargin && !caps.cmGap);
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-ltvcac");
  const profit = mode === "profit" && hasCm;
  const value = profit ? ltvCac : grossLtvCac;
  return (
    <MetricCard
      title="LTV : CAC"
      trustWarning={<DataTrustCardNotice card="LTV:CAC" />}
      requires={profit ? ["contributionMargin", "marketingSpend"] : ["marketingSpend"]}
      caps={caps}
      footerLink={footerLink}
      headerExtra={<RevenueCmToggle mode={hasCm ? mode : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      value={value}
      sub={value ? `${ltvHorizonLabel} ${profit ? "CM" : "revenue"} LTV vs ${matchedCac === null ? "matched" : moneyFmt(matchedCac)} CAC${matchedCustomers > 0 ? ` · same ${matchedCustomers.toLocaleString()} customers` : ""}${cohortNote ? ` · ${cohortNote}` : ""}` : undefined}
      sample={{ value: profit ? "3.1 : 1" : "5.0 : 1", sub: `1-year ${profit ? "CM" : "revenue"} LTV vs blended CAC` }}
      explain={[
        "<b>What it is:</b> Lifetime Value (LTV) to Customer Acquisition Cost (CAC): what one fully matured customer group became worth compared with what that exact group cost to acquire. Flip between Revenue and CM. 3:1 or better is the usual floor, and higher is better.",
        "<b>How it's calculated:</b> The picker chooses the horizon. A 90-day view uses the most recent customers who have all completed 90 days, then divides their 90-day LTV by the cost to acquire those exact people. Revenue / CM changes only customer value. The customers and acquisition cost stay identical.",
        "<b>Example:</b> $49.01 of 90-day Contribution Margin LTV divided by $18.44 to acquire those same customers = 2.66:1.",
        "<b>Note:</b> This CAC can differ from the Blended CAC card. Blended CAC follows the literal dates you picked. LTV:CAC uses the most recent fully completed cohort for that horizon. The exact customer group appears under the number. CM needs your product costs built.",
      ]}
    />
  );
}

/** MER as ONE card with a Revenue/CM toggle (was MER + CM-MER). Revenue =
 *  revenue ÷ all marketing spend; CM = contribution margin ÷ all spend. Same
 *  spend in both; only the numerator changes. */
type RatioKey = "mer" | "amer" | "cac" | "spend" | "cm-mer" | "cm-amer";
function MerCard({ mer, cmMer, basisSuffix, caps, openRatio }: {
  mer?: string; cmMer?: string; basisSuffix?: string; caps: React.ComponentProps<typeof MetricCard>["caps"];
  openRatio: (title: string, key: RatioKey) => (() => void);
}): React.ReactElement {
  const hasCm = Boolean(caps?.contributionMargin && !caps.cmGap);
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-mer");
  const profit = mode === "profit" && hasCm;
  const value = profit ? cmMer : mer;
  return (
    <MetricCard title="MER (blended)" requires={profit ? ["contributionMargin", "marketingSpend"] : ["marketingSpend"]} caps={caps}
      trustWarning={<DataTrustCardNotice card="MER" />}
      headerExtra={<RevenueCmToggle mode={hasCm ? mode : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      value={value} sub={value ? `${profit ? "contribution margin ÷ all spend" : "revenue ÷ all marketing spend"}${basisSuffix ?? ""}` : undefined}
      sample={{ value: profit ? "1.6×" : "4.2×", sub: profit ? "contribution margin ÷ all spend" : "revenue ÷ all marketing spend" }}
      trendRatio={profit ? "cm-mer" : "mer"} chartKey={profit ? "cm-mer" : "mer"} onClick={openRatio(profit ? "CM-MER" : "MER", profit ? "cm-mer" : "mer")}
      explain={[
        "<b>What it is:</b> Marketing Efficiency Ratio (MER): how hard every dollar of marketing works overall. Flip between Revenue and CM. Revenue is the classic blended Return on Ad Spend (ROAS). CM shows the margin marketing keeps, where above 1× means marketing pays for itself.",
        "<b>How it's calculated:</b> Revenue is gross revenue divided by all marketing spend. CM is total contribution margin divided by the same spend. The toggle changes only the top of the ratio; the spend stays the same.",
        "<b>Example (sample data, year-to-date):</b> Revenue is about 4.2× and CM is about 2.2× because CM counts only what each sale keeps.",
        "<b>Note:</b> Blended, so a product filter won't move it because ad spend isn't tracked per product. Revenue credits all revenue, including repeat orders, against all spend, so it flatters acquisition. That is what aMER fixes. CM needs your costs built.",
      ]} />
  );
}

/** aMER as ONE card with a Revenue/CM toggle (was aMER + CM-aMER). Revenue =
 *  new-customer first-order revenue ÷ acquisition spend; CM = first-order
 *  contribution margin ÷ the same spend. Carries the folded-in break-even. */
function AmerCard({ amer, cmAmer, revenueBreakeven, basisSuffix, caps, openRatio, footerLink }: {
  amer?: string; cmAmer?: string; revenueBreakeven?: string; basisSuffix?: string; caps: React.ComponentProps<typeof MetricCard>["caps"];
  openRatio: (title: string, key: RatioKey) => (() => void);
  footerLink?: React.ComponentProps<typeof MetricCard>["footerLink"];
}): React.ReactElement {
  const hasCm = Boolean(caps?.contributionMargin && !caps.cmGap);
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-amer");
  const profit = mode === "profit" && hasCm;
  const value = profit ? cmAmer : amer;
  const profitSub = "new-customer 1st-order CM ÷ acquisition spend · 1.0× = first-order break-even";
  const revenueSub = `new-customer revenue ÷ acquisition spend${revenueBreakeven ? ` · ${revenueBreakeven} = CM break-even` : ""}`;
  return (
    <MetricCard title="aMER (blended)" requires={profit ? ["contributionMargin", "marketingSpend"] : ["marketingSpend"]} caps={caps} footerLink={footerLink}
      trustWarning={<DataTrustCardNotice card="aMER" />}
      headerExtra={<RevenueCmToggle mode={hasCm ? mode : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      value={value} sub={value ? `${profit ? profitSub : revenueSub}${basisSuffix ?? ""}` : undefined}
      sample={{ value: profit ? "1.1×" : "2.8×", sub: profit ? "new-customer 1st-order CM ÷ acquisition spend · 1.0× = break-even" : "new-customer revenue ÷ acquisition spend · 1.8× = CM break-even" }}
      trendRatio={profit ? "cm-amer" : "amer"} chartKey={profit ? "cm-amer" : "amer"} onClick={openRatio(profit ? "CM-aMER" : "aMER", profit ? "cm-amer" : "amer")}
      explain={[
        "<b>What it is:</b> Acquisition Marketing Efficiency Ratio (aMER): the honest read on what you pay to buy customers, counting only their first order before repeat orders pad the number. Flip between Revenue and CM.",
        "<b>How it's calculated:</b> Revenue is new customers' first-order revenue divided by acquisition spend. CM is their first-order contribution margin divided by the same spend. The toggle changes only the numerator.",
        "<b>Example (sample data, year-to-date):</b> Revenue is about 1.75× and CM is about 0.92×.",
        "<b>Note:</b> Blended, so a product filter won't move it. Acquisition spend leaves out affiliate commissions on repeat orders, which are a retention cost. In CM, 1.0× is break-even. In Revenue, the matching break-even target depends on your current margin and appears below the number. The payoff from reorders shows up in the CM LTV:CAC card, not here.",
      ]} />
  );
}

/** Average order value with a Revenue/CM toggle. Revenue = net revenue ÷ orders;
 *  CM = contribution margin ÷ orders (the average PROFIT you keep per order). */
function AovCard({ aov, cmAov, loading, winLabel, caps, onRevenueDrill, onCmDrill }: {
  aov?: number; cmAov?: number; loading: boolean; winLabel: string;
  caps: React.ComponentProps<typeof MetricCard>["caps"];
  onRevenueDrill: () => void;
  onCmDrill: () => void;
}): React.ReactElement {
  const hasCm = Boolean(caps?.contributionMargin && !caps.cmGap);
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-aov");
  const cm = mode === "profit" && hasCm;
  return (
    <MetricCard title="Avg order value" requires={cm ? ["contributionMargin"] : []} caps={caps}
      headerExtra={<RevenueCmToggle mode={hasCm ? mode : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      value={cm ? (cmAov === undefined ? undefined : moneyFmt(cmAov)) : (aov === undefined ? undefined : moneyFmt(aov))}
      sub={cm ? (cmAov === undefined ? undefined : `avg CM per order · ${winLabel}`) : (aov === undefined ? undefined : winLabel)}
      loading={loading}
      trendMetric={cm ? "cm_aov" : "aov"} onClick={cm ? onCmDrill : onRevenueDrill}
      explain={[
        "<b>What it is:</b> The average size of an order. Flip between Revenue (what the order was worth) and CM (the contribution margin you actually kept on it).",
        "<b>How it's calculated:</b> Revenue mode is net revenue ÷ number of orders. CM mode is total contribution margin ÷ number of orders.",
        "<b>Example (sample data, year-to-date):</b> Revenue about $74 per order, CM about $41 after variable costs.",
        "<b>Note:</b> Revenue uses net revenue (same basis as Revenue over time). CM needs your product costs built. Product-scopable in the drill.",
      ]} />
  );
}
